/*
 * ===============================================================================================================
 *                                Copyright 2024, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { Edit, Trash, Info } from '@jda/lui-common-icon-library-mui5';
import { IconButton, Tooltip } from '@mui/material';
import { MRT_Row, MRT_RowData } from 'material-react-table';
import CustomTablePagination from '../../shared/CustomTablePagination';
import useWindowDimensions from '../../utility/useWindowDimensions';
import { useStyles } from './styles';
import { ITEMS_PER_PAGE, LABELS, EDIT_LOCATION, DELETE_LOCATION, VIEW_PASSWORD_POLICY } from './constants';
import { useDispatch, useSelector } from 'react-redux';
import { fetchLocations } from '../../store/locations/locations';
import { selectPaginationDetails } from '../../store/locations/locationsSelector';
import { checkResourcePermission } from '../../shared/CheckPermissions/CheckPermission';
import { actions as resourceActions, resources } from '../../shared/CheckPermissions/constants';
import { selectResourcePermissions } from '../../store/auth/authSelector';

interface Location {
  id: string;
  name: string;
  locationType: string;
  locationAddress: string;
}

interface LocationTableProps {
  data: { locations: Location[], loading: boolean };
  onLocationDelete: (locationId: string) => void;
  onLocationEdit: (locationId: string) => void;
  onLocationsPasswordPolicyView: (locationId: string, name: string) => void;
}

const LocationTable = ({
  data: { locations, loading },
  onLocationDelete,
  onLocationEdit,
  onLocationsPasswordPolicyView
}: LocationTableProps) => {
  const { pageNumber, pageSize, ...pageDetails } = useSelector(selectPaginationDetails);
  const resourcePermissions = useSelector(selectResourcePermissions);

  const { height } = useWindowDimensions();
  const dispatch = useDispatch();

  const isEditLocationPolicyAllowed =
    resourcePermissions &&
    resourcePermissions.resources &&
    checkResourcePermission(resourceActions.EDIT, resourcePermissions.resources[resources.LOCATIONS]);

  const isDeleteLocationPolicyAllowed =
    resourcePermissions &&
    resourcePermissions.resources &&
    checkResourcePermission(resourceActions.DELETE, resourcePermissions.resources[resources.LOCATIONS]);

  const isPasswordPolicyViewAllowed =
    resourcePermissions &&
    resourcePermissions.resources &&
    checkResourcePermission(resourceActions.VIEW, resourcePermissions.resources[resources.PASSWORD_POLICY]);

  const { classes } = useStyles();
  const onChangePageOptions = (itemsPerPage: number, page: number) => {
    const pageParams = {
      ...pageDetails,
      pageNumber: page,
      pageSize: itemsPerPage
    };
    fetchLocations(dispatch, pageParams);
  };

  return (
    <CustomTablePagination
      rowsPerPageOptions={[20, 50, 100]}
      labelRowsPerPage={ITEMS_PER_PAGE}
      data={locations}
      loading={loading}
      columns={[
        {
          header: LABELS.DISPLAY_NAME,
          accessorKey: 'displayName',
          muiTableHeadCellProps: {
            sx: {
              width: '20%'
            }
          }
        },
        {
          header: LABELS.LOCATION_NAME,
          accessorKey: 'name',
          muiTableHeadCellProps: {
            sx: {
              width: '20%'
            }
          }
        },
        {
          header: LABELS.LOCATION_TYPE,
          accessorKey: 'locationType',
          muiTableHeadCellProps: {
            sx: {
              width: '20%'
            }
          }
        },
        // {
        //   header: LABELS.LOCATION_ADDRESS,
        //   accessorKey: 'locationAddress',
        //   muiTableHeadCellProps: {
        //     sx: {
        //       width: '20%'
        //     }
        //   }
        // },
        isPasswordPolicyViewAllowed && {
          header: LABELS.PASSWORD_POLICY,
          accessorKey: 'passwordPolicy',
          Cell: ({ row }: { row: MRT_Row<MRT_RowData> }) =>
            row.original.idpId ? (
              <IconButton
                aria-label={VIEW_PASSWORD_POLICY}
                size="small"
                onClick={() => onLocationsPasswordPolicyView(row.original.passwordPolicyId, row.original.name)}
              >
                <Info color="secondary" className={classes.icon} />
              </IconButton>
            ) : null,
          muiTableHeadCellProps: {
            sx: {
              width: '20%'
            }
          }
        },
        isEditLocationPolicyAllowed && {
          header: '',
          accessorKey: 'edit',
          muiTableHeadCellProps: {
            sx: {
              width: '3%'
            }
          },
          Cell: ({ row }: { row: MRT_Row<MRT_RowData> }) => (
            <Tooltip arrow title={EDIT_LOCATION} placement="bottom">
              <IconButton
                aria-label={EDIT_LOCATION}
                size="small"
                onClick={() => {
                  onLocationEdit(row.original.id);
                }}
              >
                <Edit color="secondary" className={classes.icon} />
              </IconButton>
            </Tooltip>
          )
        },
        isDeleteLocationPolicyAllowed && {
          header: '',
          accessorKey: 'delete',
          muiTableHeadCellProps: {
            sx: {
              width: '3%'
            }
          },
          Cell: ({ row }: { row: MRT_Row<MRT_RowData> }) => (
            <Tooltip arrow title={DELETE_LOCATION} placement="bottom">
              <IconButton
                aria-label={DELETE_LOCATION}
                size="small"
                onClick={() => {
                  onLocationDelete(row.original.id);
                }}
              >
                <Trash color="secondary" className={classes.icon} />
              </IconButton>
            </Tooltip>
          )
        }
      ].filter(Boolean)}
      maxTableBodyHeight={height - 35 * 8}
      currentPageNumber={pageNumber}
      currentItemsPerPage={pageSize}
      onChangePageOptions={(itemsPerPage: number, page: number) => {
        onChangePageOptions(itemsPerPage, page);
      }}
      totalCount={locations.length}
    />
  );
};

export default LocationTable;
